import {
  DEFAULT_PRINT_SETTINGS,
  type ReaderSettings,
} from "@nevisign/reader-core";
import { ArrowRight, Settings2 } from "lucide-react";
import { DEFAULT_NEVISIGN_API_BASE } from "../lib/constants";
import { useAuth } from "../contexts/auth-context";

type Props = {
  settings: ReaderSettings;
  onChange: (partial: Partial<ReaderSettings>) => void;
  versionLabel?: string;
  onBack?: () => void;
};

export function SettingsPanel({
  settings,
  onChange,
  versionLabel,
  onBack,
}: Props) {
  const print = settings.print ?? DEFAULT_PRINT_SETTINGS;
  const { user, isAuthenticated, openLogin, logout } = useAuth();

  return (
    <div className={`settings-page consumer-shell${onBack ? " settings-page-full" : ""}`}>
      {onBack ? (
        <header className="list-page-hero">
          <button type="button" className="text-back" onClick={onBack}>
            <ArrowRight size={16} />
            بازگشت
          </button>
          <div className="list-page-hero-copy">
            <span className="consumer-kicker">
              <Settings2 size={14} />
              تنظیمات
            </span>
            <h1>{import.meta.env.DEV ? "حساب، اتصال و نمایش" : "حساب و نمایش"}</h1>
            <p>
              {import.meta.env.DEV
                ? "کنترل حساب نویساین، آدرس API، نمای Word و پیش‌فرض‌های چاپ — بدون شلوغی داشبورد."
                : "کنترل حساب نویساین، نمای Word و پیش‌فرض‌های چاپ — بدون شلوغی داشبورد."}
            </p>
          </div>
        </header>
      ) : (
        <section className="consumer-panel">
          <span className="consumer-kicker">تنظیمات تجربه کاربری</span>
          <h2 className="consumer-title" style={{ fontSize: "1.1rem", marginTop: "0.5rem" }}>
            کنترل حساب، محیط و رفتار نمایش
          </h2>
          <p className="consumer-lead" style={{ margin: 0 }}>
            همه چیزهایی که روی نمایش اسناد، چاپ و حساب اثر می‌گذارند از اینجا قابل
            تنظیم‌اند.
          </p>
        </section>
      )}

      <section className="settings-group-card" aria-label="حساب">
        <h3 className="settings-section-title">حساب نویساین</h3>
        <div className="settings-field">
          {isAuthenticated && user ? (
            <>
              <p className="settings-hint">
                {user.fullName}
                {user.organizationName ? ` · ${user.organizationName}` : ""}
              </p>
              <button type="button" className="ghost-btn" onClick={() => void logout()}>
                خروج از حساب
              </button>
            </>
          ) : (
            <>
              <p className="settings-hint">
                برای اسناد ابری، دستیار و ابزارهای هوش مصنوعی وارد شوید. خواندن فایل
                محلی بدون ورود ممکن است.
              </p>
              <button type="button" className="primary-btn" onClick={() => openLogin()}>
                ورود به حساب
              </button>
            </>
          )}
        </div>

        {versionLabel ? (
          <div className="settings-field">
            <label>نسخه برنامه</label>
            <p className="settings-hint" dir="ltr">
              {versionLabel}
            </p>
          </div>
        ) : null}
      </section>

      {import.meta.env.DEV ? (
        <section className="settings-group-card" aria-label="اتصال">
          <h3 className="settings-section-title">اتصال</h3>
          <div className="settings-field">
            <label htmlFor="api-base">آدرس پایه API نویساین</label>
            <input
              id="api-base"
              type="text"
              dir="ltr"
              value={settings.nevisignApiBase}
              placeholder={DEFAULT_NEVISIGN_API_BASE}
              onChange={(e) => onChange({ nevisignApiBase: e.target.value })}
            />
            <p className="settings-hint">
              پیش‌فرض محلی: <code dir="ltr">{DEFAULT_NEVISIGN_API_BASE}</code> از طریق
              پروکسی Vite به API پنل.
            </p>
          </div>
        </section>
      ) : null}

      <section className="settings-group-card" aria-label="نمایش">
        <h3 className="settings-section-title">نمایش سند</h3>
        <div className="settings-field">
          <label htmlFor="docx-view-mode">نمای Word</label>
          <select
            id="docx-view-mode"
            value={settings.docxViewMode}
            onChange={(e) =>
              onChange({
                docxViewMode: e.target.value as ReaderSettings["docxViewMode"],
              })
            }
          >
            <option value="continuous">پیوسته (جریان متن)</option>
            <option value="paginated">صفحه‌بندی (شبیه چاپ)</option>
          </select>
        </div>

        <div className="settings-field">
          <label>
            <input
              type="checkbox"
              checked={settings.showTrackChanges}
              onChange={(e) => onChange({ showTrackChanges: e.target.checked })}
            />{" "}
            نمایش تغییرات (Track Changes)
          </label>
        </div>

        <div className="settings-field">
          <label>
            <input
              type="checkbox"
              checked={settings.showComments}
              onChange={(e) => onChange({ showComments: e.target.checked })}
            />{" "}
            هایلایت یادداشت‌ها در متن
          </label>
        </div>
      </section>

      <section className="settings-group-card" aria-label="چاپ">
        <h3 className="settings-section-title">پیش‌فرض چاپ</h3>
        <div className="settings-field">
          <label htmlFor="def-print-size">اندازه صفحه</label>
          <select
            id="def-print-size"
            value={print.pageSize}
            onChange={(e) =>
              onChange({
                print: {
                  ...print,
                  pageSize: e.target.value as typeof print.pageSize,
                },
              })
            }
          >
            <option value="A4">A4</option>
            <option value="Letter">Letter</option>
          </select>
        </div>
        <div className="settings-field">
          <label htmlFor="def-print-orient">جهت</label>
          <select
            id="def-print-orient"
            value={print.orientation}
            onChange={(e) =>
              onChange({
                print: {
                  ...print,
                  orientation: e.target.value as typeof print.orientation,
                },
              })
            }
          >
            <option value="portrait">عمودی</option>
            <option value="landscape">افقی</option>
          </select>
        </div>
        <div className="settings-field">
          <label htmlFor="def-print-margin">حاشیه (میلی‌متر)</label>
          <input
            id="def-print-margin"
            type="number"
            min={0}
            max={40}
            value={print.marginMm}
            onChange={(e) =>
              onChange({
                print: {
                  ...print,
                  marginMm: Math.max(0, Number(e.target.value) || 0),
                },
              })
            }
          />
        </div>
        <div className="settings-field">
          <label htmlFor="def-print-color">رنگ</label>
          <select
            id="def-print-color"
            value={print.colorMode}
            onChange={(e) =>
              onChange({
                print: {
                  ...print,
                  colorMode: e.target.value as typeof print.colorMode,
                },
              })
            }
          >
            <option value="color">رنگی</option>
            <option value="grayscale">سیاه‌وسفید</option>
          </select>
        </div>
        <div className="settings-field">
          <label>
            <input
              type="checkbox"
              checked={print.includeComments}
              onChange={(e) =>
                onChange({
                  print: { ...print, includeComments: e.target.checked },
                })
              }
            />{" "}
            نمایش یادداشت‌ها در چاپ
          </label>
        </div>
      </section>
    </div>
  );
}
